import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ApiConfig } from '@kildenconfig/api-config';
import { AppConfig } from '@kildenconfig/app.config';
import { CatalogTreeItem } from '@kildenshared/components/catalog-tree/catalog-tree-item';
import { CatalogLayerVariantInterface } from '@kildenshared/interfaces/catalog-layer-variant.interface';
import { CatalogLayerVariantsInterface } from '@kildenshared/interfaces/catalog-layer-variants.interface';
import { catchError, map, Observable, retry, take, throwError } from 'rxjs';
import Swal from 'sweetalert2';
import { MapService } from '../map.service';
import { TicketService } from '../ticket.service';

@Injectable({
  providedIn: 'root',
})
export class OrthoProjectsService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _mapService = inject(MapService);
  private readonly _ticketService = inject(TicketService);

  getProjects(type: 'flyPhotos' | 'satPhotos' = 'flyPhotos'): Observable<ProjectMetadataInterface> {
    const orthophotoTypes = type === 'satPhotos' ? [6] : [1, 2, 3, 4, 5, 8, 9, 12];

    const mapExtent = this._mapService.getViewExtent();
    const srId = this._mapService.getMap().getView().getProjection().getCode().split(':')[1] || '25832';

    if (!mapExtent) {
      throw new Error('Could not get map extent for fetching project meta data');
    }

    const minY = mapExtent[1];
    const maxY = mapExtent[3];
    const minX = mapExtent[0];
    const maxX = mapExtent[2];

    const baseRequestBody: ProjectsRequestInterface = {
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [minX, minY],
            [minX, maxY],
            [maxX, maxY],
            [maxX, minY],
            [minX, minY],
          ],
        ],
      },
      projects: [],
      inputWkid: Number(srId),
      outputWkid: Number(srId),
      returnMetadata: true,
      returnGeometry: false,
      inCacheOnly: false,
      stopOnCover: false,
    };

    return this._httpClient
      .post<ProjectsResponseInterface>(this._resolveProjectsUrl(), baseRequestBody, {
        headers: this._buildHeaders(),
      })
      .pipe(
        map(response => {
          const projectList = (response?.projects ?? [])
            .filter(project => {
              const orthoType = project.metadata?.orthophotoType;
              return typeof orthoType === 'number' && orthophotoTypes.includes(orthoType);
            })
            .sort((a, b) => {
              const yearA = a.metadata?.year ?? 0;
              const yearB = b.metadata?.year ?? 0;
              if (yearA !== yearB) {
                return yearB - yearA;
              }

              const dateA = Date.parse(a.metadata?.photoDate ?? '') || 0;
              const dateB = Date.parse(b.metadata?.photoDate ?? '') || 0;
              if (dateA !== dateB) {
                return dateB - dateA;
              }

              return a.projectName.localeCompare(b.projectName, 'nb');
            })
            .map(project => project.projectName)
            .filter(Boolean);

          return {
            Success: true,
            ProjectList: Array.from(new Set(projectList)),
            ProjectMetadata: {
              type,
              orthophotoTypes,
              endpoint: this._resolveProjectsUrl(),
              inputWkid: Number(srId),
              resultCount: projectList.length,
            },
          } as ProjectMetadataInterface;
        }),
        take(1),
        retry({ count: 3, delay: 500, resetOnSuccess: true }),
        catchError((err: Error) => {
          this._errorPopup(err as HttpErrorResponse);
          console.error(`caught err`, err);
          return throwError(() => err);
        })
      );
  }

  getProjectsAsLayerVariants(treeItem: CatalogTreeItem): Observable<CatalogLayerVariantsInterface> {
    const itemList: CatalogLayerVariantInterface[] = [];
    let orthoType: 'flyPhotos' | 'satPhotos' = 'satPhotos';

    // Exclusive to flyPhotos
    if ((treeItem.idBod as string) === AppConfig.IDBOD_NIB_2) {
      orthoType = 'flyPhotos';
      itemList.push({
        label: 'Heldekkende nyeste',
        value: 'ortofoto',
      } as CatalogLayerVariantInterface);
    }

    let variants = {
      default: itemList[0],
      items: itemList,
      selected: itemList[0],
    } as CatalogLayerVariantsInterface;

    // console.log(`${treeItem.idBod}: getProjects(${orthoType})`);

    return this.getProjects(orthoType).pipe(
      map(response => {
        if (!response || !treeItem?.config || !treeItem?.idBod) {
          return variants;
        }

        if (!response.Success || response.ProjectList.length < 1) {
          return variants;
        }

        // Populate list
        response.ProjectList.forEach(item => {
          itemList.push({
            label: item,
            value: item,
          } as CatalogLayerVariantInterface);
        });

        // Set default: always use the first item (newest after sorting)
        const defaultItem = itemList[0];

        // Save to tree/catalog
        variants = {
          default: defaultItem,
          items: itemList,
          selected: defaultItem,
        } as CatalogLayerVariantsInterface;

        // console.log(itemList.length + ` new orthoprojects, last one:`, itemList[itemList.length - 1].label);

        return variants;
      }),
      take(1),
      catchError((e: Error) => {
        console.error(`caught err`, e);
        return throwError(() => e);
      })
    );
  }

  private _errorPopup(err: HttpErrorResponse) {
    Swal.fire({
      title: ApiConfig.defaultErrorHeader,
      html: `Henting av flyfoto-data feilet: <BR> ${err.message}`,
      icon: 'error',
      confirmButtonText: 'OK',
    });
  }

  private _resolveProjectsUrl(): string {
    return ApiConfig.nibProjectsMetadataUrl;
  }

  private _buildHeaders(): HttpHeaders {
    const token = this._ticketService.getNibToken();
    const headers: Record<string, string> = {
      accept: 'application/json',
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return new HttpHeaders(headers);
  }
}

export interface ProjectMetadataInterface {
  Success: boolean;
  ErrorMessage?: string;
  ProjectList: string[];
  ProjectMetadata?: ProjectMetadataDetailsInterface;
}

export interface ProjectMetadataDetailsInterface {
  type: 'flyPhotos' | 'satPhotos';
  orthophotoTypes: number[];
  endpoint: string;
  inputWkid: number;
  resultCount: number;
}

interface ProjectsRequestInterface {
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  projects: string[];
  inputWkid: number;
  outputWkid: number;
  returnMetadata: boolean;
  returnGeometry: boolean;
  inCacheOnly: boolean;
  stopOnCover: boolean;
}

interface ProjectsResponseInterface {
  projects: {
    projectName: string;
    metadata?: {
      orthophotoType?: number;
      year?: number;
      photoDate?: string;
    };
  }[];
}
